import { useState } from 'react';
import { supabase } from '../lib/supabase';
import axios from 'axios';
import type { PodcastTranscription, PodcastChapter } from '../types/podcasts';

function usePodcastTranscription() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTranscription = async (podcastId: string, language: string = 'pt') => {
    try {
      setLoading(true);
      const { data: transcriptions, error: transcriptionError } = await supabase
        .from('podcast_transcriptions')
        .select('*')
        .eq('podcast_id', podcastId)
        .eq('language', language);

      if (transcriptionError) throw transcriptionError;
      
      if (!transcriptions || transcriptions.length === 0) {
        throw new Error('No transcription found');
      }
      
      // Return the most recent transcription
      return transcriptions[0] as PodcastTranscription;
    } catch (err) {
      console.error('Error fetching transcription:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // New function to check if a valid transcription exists
  const checkTranscriptionValidity = async (podcastId: string, language: string = 'pt') => {
    try {
      setLoading(true);
      const { data: transcriptions, error: transcriptionError } = await supabase
        .from('podcast_transcriptions')
        .select('*')
        .eq('podcast_id', podcastId)
        .eq('language', language)
        .order('created_at', { ascending: false })
        .limit(1);

      if (transcriptionError) throw transcriptionError;
      
      const existingTranscription = transcriptions?.[0];
      
      // Check if transcription exists and is valid (completed status and has content)
      if (existingTranscription && 
          existingTranscription.status === 'completed' && 
          existingTranscription.content && 
          existingTranscription.content.trim().length > 0) {
        return {
          isValid: true,
          transcription: existingTranscription as PodcastTranscription
        };
      }
      
      // If transcription is being processed, return it but mark as not valid yet
      if (existingTranscription && existingTranscription.status === 'processing') {
        return {
          isValid: false,
          transcription: existingTranscription as PodcastTranscription,
          message: 'A transcrição está a ser processada processada.'
        };
      }
      
      // If transcription failed, return it with error message
      if (existingTranscription && existingTranscription.status === 'failed') {
        return {
          isValid: false,
          transcription: existingTranscription as PodcastTranscription,
          message: `A transcrição anterior falhou: ${existingTranscription.error || 'Erro desconhecido'}`
        };
      }
      
      // No transcription found
      return {
        isValid: false,
        message: 'Nenhuma transcrição encontrada.'
      };
    } catch (err) {
      console.error('Error checking transcription validity:', err);
      return {
        isValid: false,
        message: err instanceof Error ? err.message : 'Erro ao verificar transcrição'
      };
    } finally {
      setLoading(false);
    }
  };

  const fetchChapters = async (podcastId: string) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('podcast_chapters')
        .select('*')
        .eq('podcast_id', podcastId)
        .order('start_time', { ascending: true });

      if (error) throw error;
      return data as PodcastChapter[];
    } catch (err) {
      console.error('Error fetching chapters:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const generateTranscription = async (podcastId: string, audioUrl: string) => {
    let transcriptionRecord = null;
    console.log('Starting transcription generation...');
    console.log('Audio URL:', audioUrl);

    try {
      setLoading(true);
      setError(null);

      // Validate audio URL
      if (!audioUrl || !audioUrl.trim()) {
        throw new Error('URL do áudio é obrigatória');
      }

      // Ensure URL is properly formatted
      try {
        new URL(audioUrl);
      } catch (e) {
        throw new Error('URL do áudio inválida');
      }

      // Check for existing transcription
      const { data: existingTranscriptions } = await supabase
        .from('podcast_transcriptions')
        .select('*')
        .eq('podcast_id', podcastId)
        .eq('language', 'pt')
        .order('created_at', { ascending: false })
        .limit(1);

      const existingTranscription = existingTranscriptions?.[0];
      
      if (existingTranscription?.status === 'completed') {
        console.log('Completed transcription already exists');
        return existingTranscription as PodcastTranscription;
      }
      
      if (existingTranscription?.status === 'processing') {
        console.log('Transcription is already being processed');
        return existingTranscription as PodcastTranscription;
      }

      // Update existing record or create new one
      const { data: record, error: upsertError } = await supabase
        .from('podcast_transcriptions')
        .upsert({
          id: existingTranscription?.id,
          podcast_id: podcastId,
          content: existingTranscription?.content || '',
          language: 'pt',
          status: 'processing',
          error: null,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'podcast_id,language',
          ignoreDuplicates: false
        })
        .select()
        .single();

      if (upsertError) throw upsertError;
      transcriptionRecord = record;

      console.log('Making request to Gladia API...');
      // Create FormData object
      const formData = new FormData();
      formData.append('audio_url', audioUrl);
      formData.append('language', 'pt');
      formData.append('language_behaviour', 'automatic single language');
      formData.append('toggle_diarization', 'true'); 
      formData.append('diarization_max_speakers', '2');
      formData.append('output_format', 'json');
      formData.append('target_translation_language', 'pt');
      
      const response = await axios.post(
        'https://api.gladia.io/audio/text/audio-transcription/',
        formData,
        {
          headers: {
            'x-gladia-key': 'e461554e-4661-4897-8898-85e53e914d2c',
            'Content-Type': 'multipart/form-data'
          },
          timeout: 300000 // 5 minute timeout
        }
      );

      console.log('Gladia API response:', response.data);

      // Update transcription record with processing status
      await supabase
        .from('podcast_transcriptions')
        .update({
          status: 'processing',
          updated_at: new Date().toISOString()
        })
        .eq('id', transcriptionRecord.id);

      // Process the transcription
      // Initialize with empty string - we'll add content properly during processing
      let transcriptionText = '';
      
      // Process main content
      if (response.data.prediction) {
        // Format time function for timestamps
        const formatTime = (seconds: number | string | undefined): string => {
          // Handle undefined case
          if (seconds === undefined) {
            return '00:00';
          }
          
          // Ensure we're working with a valid number
          if (typeof seconds === 'string') {
            seconds = parseFloat(seconds);
          }
          
          if (typeof seconds !== 'number' || isNaN(seconds)) {
            seconds = 0;
          }
          
          const minutes = Math.floor(seconds / 60);
          const secs = Math.floor(seconds % 60);
          // Removed milliseconds as requested
          return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        };

        // Group segments by speaker and format like Gladia's output
        let currentSpeaker = '';
        let currentText = '';
        let currentStartTime = 0;
        const segments: {speaker: string, text: string, startTime: number}[] = [];

        // Define the standard intro/outro text that should always be present
        const standardText = 'As personagens, os momentos, as emoções, todas as histórias cabem no sinopse, o podcast do agrupamento de Escolas de Albergaria a Velha.';

        // First, scan all segments to find and extract the intro text
        let hasFoundIntro = false;
        let introSegment = null;
        
        // Process each prediction segment
        response.data.prediction.forEach((segment: any, index: number) => {
          const speaker = segment.speaker || '';
          let text = segment.transcription || segment.text || '';
          
          // Fix: Ensure we're correctly extracting the start_time from the API response
          // The API might be returning start_time in a different format or property
          let startTime = 0;
          
          // Check all possible properties where start_time might be stored
          if (segment.time_begin !== undefined) {
            startTime = typeof segment.time_begin === 'number' ? segment.time_begin : parseFloat(segment.time_begin);
          } else if (segment.start_time !== undefined) {
            startTime = typeof segment.start_time === 'number' ? segment.start_time : parseFloat(segment.start_time);
          } else if (segment.start !== undefined) {
            startTime = typeof segment.start === 'number' ? segment.start : parseFloat(segment.start);
          } else if (segment.time !== undefined) {
            startTime = typeof segment.time === 'number' ? segment.time : parseFloat(segment.time);
          }
          
          console.log(`Raw segment ${index}: speaker=${speaker}, time_begin=${segment.time_begin}, parsed=${startTime}`);
          
          // Check if this segment contains the standard text or parts of it
          if (text.includes('Albergaria a Velha') && !hasFoundIntro) {
            // Extract the standard text and create a separate intro segment
            hasFoundIntro = true;
            
            // Create intro segment with the actual timestamp from the API
            introSegment = {
              speaker: 'INTRO',
              text: standardText,
              startTime: startTime // Preserve the actual timestamp from the API
            };
          }
          
          // Always remove any instances of the standard text from all segments
          // This ensures the standard text only appears in dedicated intro/outro segments
          if (text.includes(standardText)) {
            text = text.replace(standardText, '').trim();
          } else if (text.includes('As personagens') && text.includes('Albergaria a Velha')) {
            // Try to match partial standard text with more comprehensive regex
            // This will catch variations of the standard text
            const regex = /As personagens[^.]*Albergaria a Velha\.[^\n]*/g;
            text = text.replace(regex, '').trim();
          }
          
          // Enhanced check for any remaining fragments of the intro text
          if (text.includes('As personagens') || text.includes('os momentos') || text.includes('as emoções') || 
              text.includes('todas as histórias') || text.includes('cabem no sinopse') || 
              text.includes('o podcast do agrupamento') || text.includes('Escolas de Albergaria a Velha') ||
              text.includes('sinopse') || text.includes('podcast') || text.includes('agrupamento')) {
            
            // More comprehensive regex to catch all possible fragments
            const partialRegex = /As personagens[^\n]*|os momentos[^\n]*|as emoções[^\n]*|todas as histórias[^\n]*|cabem no sinopse[^\n]*|podcast do agrupamento[^\n]*|[^\n]*Escolas de Albergaria a Velha[^\n]*/g;
            text = text.replace(partialRegex, '').trim();
            
            // Additional cleanup for any remaining fragments - more aggressive removal
            text = text.replace(/\bsinopse\b[^\n]*/g, '').trim();
            text = text.replace(/\bpodcast\b[^\n]*/g, '').trim();
            text = text.replace(/\bagrupamento\b[^\n]*/g, '').trim();
            text = text.replace(/\bEscolas\b[^\n]*/g, '').trim();
            text = text.replace(/\bAlbergaria\b[^\n]*/g, '').trim();
            text = text.replace(/\bVelha\b[^\n]*/g, '').trim();
            text = text.replace(/\bhistórias\b[^\n]*/g, '').trim();
          }
          
          // Skip empty segments after removing intro text
          if (text.trim() === '') {
            return;
          }
          
          // If speaker changes or this is the first segment, start a new segment
          if (speaker !== currentSpeaker || currentText === '') {
            if (currentText !== '') {
              segments.push({
                speaker: currentSpeaker,
                text: currentText,
                startTime: currentStartTime
              });
            }
            currentSpeaker = speaker;
            currentText = text;
            currentStartTime = startTime;
          } else {
            // Continue with the same speaker
            currentText += text;
          }
        });

        // Add the last segment
        if (currentText !== '') {
          segments.push({
            speaker: currentSpeaker,
            text: currentText,
            startTime: currentStartTime
          });
        }

        // Format the content like Gladia's output with improved speaker identification
        
        // Always add the intro segment as the first segment
        // If we found one in the transcription, use that; otherwise create a new one
        if (introSegment) {
          segments.unshift(introSegment);
        } else if (segments.length > 0) {
          segments.unshift({
            speaker: 'INTRO',
            text: standardText,
            startTime: 0
          });
        }
        
        // Check for Out Intro segment (second occurrence of standard text)
        let hasOutIntro = false;
        for (let i = 1; i < segments.length - 1; i++) {
          if (segments[i].text && segments[i].text.includes('Albergaria a Velha')) {
            hasOutIntro = true;
            // Ensure it's labeled as OUT_INTRO
            segments[i].speaker = 'OUT_INTRO';
            break;
          }
        }
        
        // If no Out Intro was found, try to add one before the last segment
        if (!hasOutIntro && segments.length > 2) {
          // Calculate a reasonable timestamp for the out intro
          const lastSegmentIndex = segments.length - 1;
          const lastSegmentTime = segments[lastSegmentIndex].startTime || 0;
          const secondLastSegmentTime = segments[lastSegmentIndex - 1].startTime || 0;
          const outIntroTime = lastSegmentTime - 30; // 30 seconds before the last segment
          
          // Add out intro with the standard text
          segments.splice(lastSegmentIndex, 0, {
            speaker: 'OUT_INTRO',
            text: standardText,
            startTime: outIntroTime > secondLastSegmentTime ? outIntroTime : secondLastSegmentTime + 10
          });
        }
        
        // Always add an outro segment at the end if there isn't one already
        const lastSegment = segments[segments.length - 1];
        const hasOutro = lastSegment && String(lastSegment.speaker).toUpperCase() === 'OUTRO';
        
        if (!hasOutro && segments.length > 0) {
          // Calculate a reasonable timestamp for the outro based on the last segment
          // Instead of using a fixed high value (999999)
          const lastSegmentTime = lastSegment ? (lastSegment.startTime || 0) : 0;
          const outroTime = lastSegmentTime + 60; // Add 60 seconds to the last segment's time
          
          // Add outro with the standard text and a reasonable timestamp
          segments.push({
            speaker: 'OUT_INTRO', // Use consistent uppercase format for special segments
            text: standardText,
            startTime: outroTime
          });
        }
        
        const hasStandardIntro = segments.length > 0 && segments[0].text.includes('Albergaria a Velha');
        const hasStandardOutro = segments.length > 1 && segments[segments.length - 1].text.includes('Albergaria a Velha');
        
        // Process segments with proper speaker identification and use actual timestamps from the API
        // No need to calculate average segment duration as we're using actual timestamps
        
        // Determine if this is a single-speaker podcast (only Convidado between intro and outro)
        // We'll check if there are only intro/outro segments plus one other speaker segment
        const nonIntroOutroSegments = segments.filter(seg => 
          !String(seg.text || '').includes('Albergaria a Velha')
        );
        
        // If we have only one or two non-intro/outro segments, treat as single-speaker podcast
        const isSingleSpeakerPodcast = nonIntroOutroSegments.length <= 2;
        console.log(`Detected ${isSingleSpeakerPodcast ? 'single' : 'multi'}-speaker podcast with ${nonIntroOutroSegments.length} non-intro/outro segments`);
        
        const processedSegments = segments.map((segment, index, array) => {
          // Handle case where speaker might not be a string
          const speakerStr = String(segment.speaker || '');
          const speakerNum = speakerStr.replace('SPEAKER_0', '');
          
          // Identify special segments (intro, out intro, and outro)
          let speakerLabel;
          
          // First segment is intro if it contains the standard text
          if (index === 0 && segment.text.includes('Albergaria a Velha')) {
            speakerLabel = 'Intro';
          } 
          // Check if this is an Out Intro segment (from OUT_INTRO speaker)
          else if (String(segment.speaker).toUpperCase() === 'OUT_INTRO' || 
                  (index !== 0 && segment.text.includes('Albergaria a Velha'))) {
            speakerLabel = 'Out Intro';
            // Ensure Out Intro always has the complete standard text
            segment.text = standardText;
            console.log('Setting Out Intro text to standard text:', standardText);
          }
          // Last segment is outro if it contains the standard text
          else if (index === array.length - 1 && segment.text.includes('Albergaria a Velha')) {
            speakerLabel = 'Out Intro';
            // Ensure the last segment also has the complete standard text
            segment.text = standardText;
          }
          // For single-speaker podcasts, attribute all non-intro/outro content to Convidado
          else if (isSingleSpeakerPodcast) {
            speakerLabel = 'Convidado';
          }
          // Apply alternating Entrevistador/Convidado pattern for multi-speaker podcasts
          else {
            // Calculate the effective index (skipping intro)
            const effectiveIndex = hasStandardIntro ? index - 1 : index;
            
            // Apply alternating pattern: Entrevistador, Convidado, Entrevistador, etc.
            if (effectiveIndex % 2 === 0) {
              speakerLabel = 'Entrevistador';
            } else {
              speakerLabel = 'Convidado';
            }
            
            // Only clean intro text fragments from segments, but preserve the actual content
            if (segment.text) {
              // Only remove standard phrases if they appear to be intro/outro fragments
              const standardPhrases = [
                'todas as histórias cabem no sinopse',
                'O podcast do agrupamento',
                'Escolas de Albergaria a Velha',
                'As personagens',
                'os momentos',
                'as emoções'
              ];
              
              let cleanedText = segment.text;
              
              // Only clean if the text contains fragments that look like intro/outro
              if (cleanedText.includes('sinopse') || 
                  cleanedText.includes('podcast') || 
                  cleanedText.includes('Albergaria')) {
                  
                standardPhrases.forEach(phrase => {
                  cleanedText = cleanedText.replace(new RegExp(phrase, 'gi'), '');
                });
                
                // Clean up any remaining fragments or punctuation issues
                cleanedText = cleanedText.replace(/\.\s*\./g, '.'); // Fix double periods
                cleanedText = cleanedText.replace(/\s+/g, ' '); // Fix multiple spaces
              }
              
              segment.text = cleanedText.trim();
            }
          }
          
          // Ensure we have a valid timestamp - Debug the timestamp value
          console.log(`Formatting timestamp for segment with startTime: ${segment.startTime}`);
          
          // Make sure we're working with a valid number for the timestamp
          let timeValue = segment.startTime;
          if (typeof timeValue === 'string') {
            timeValue = parseFloat(timeValue);
          }
          if (typeof timeValue !== 'number' || isNaN(timeValue)) {
            timeValue = 0;
          }
          
          const timestamp = formatTime(timeValue);
          console.log(`Formatted timestamp: ${timestamp}`);
          
          return {
            label: speakerLabel,
            formattedText: `${speakerLabel} | ${timestamp}\n${segment.text}`,
            isStandardText: segment.text.includes('Albergaria a Velha')
          };
        });
        
        // Always include the intro, out intro, and outro, and ensure they're properly separated from other content
        const contentText = processedSegments
          .filter(segment => {
            // Keep intro, out intro, and outro segments even if they contain standard text
            if (segment.label === 'Intro' || segment.label === 'Out Intro' || segment.label === 'Outro') {
              return true;
            }
            
            // Always include Entrevistador segments, even if they're empty
            if (segment.label === 'Entrevistador') {
              return true;
            }
            
            // For other segments, filter out any that still contain the standard text
            // Add null check to prevent error when segment.text is undefined
            const containsStandardText = segment.text && (
              segment.text.includes(standardText) ||
              segment.text.includes('todas as histórias cabem no sinopse') ||
              segment.text.includes('o podcast do agrupamento')
            );
            
            return !containsStandardText;
          })
          .map(segment => segment.formattedText)
          .join('\n\n');
          
        // Ensure we have an Out Intro section in the transcription
        if (!contentText.includes('Out Intro |')) {
          // Find the position to insert the Out Intro - before the Outro if it exists
          const outroPosition = contentText.lastIndexOf('Outro |');
          if (outroPosition > 0) {
            // Insert Out Intro before Outro
            const beforeOutro = contentText.substring(0, outroPosition);
            const outroAndAfter = contentText.substring(outroPosition);
            const outIntroText = `Out Intro | 00:00\n${standardText}`;
            transcriptionText = `${beforeOutro}\n\n${outIntroText}\n\n${outroAndAfter}`;
          } else {
            // No Outro found, add Out Intro at the end
            const outIntroText = `Out Intro | 00:00\n${standardText}`;
            transcriptionText = `${contentText}\n\n${outIntroText}`;
          }
        } else {
          // Out Intro already exists in the content, but ensure it has the complete standard text
          const outIntroRegex = /(Out Intro \| \d+:\d+)\n([^\n]*)/g;
          transcriptionText = contentText.replace(outIntroRegex, `$1\n${standardText}`);
        }
        
        // Double-check to ensure Out Intro is present in the final transcription
        if (!transcriptionText.includes('Out Intro |')) {
          // If still missing, forcefully append it at the end
          const outIntroText = `\n\nOut Intro | 00:00\n${standardText}`;
          transcriptionText += outIntroText;
        }
          
        // Ensure the intro is properly formatted with a clear separation
        if (contentText.includes('Intro |')) {
          const parts = contentText.split('Intro |');
          if (parts.length > 1) {
            const introPart = 'Intro |' + parts[1].split('\n\n')[0];
            
            // Get the rest of the content, excluding any parts that contain the intro text
            let restPart = '';
            if (parts[1].includes('\n\n')) {
              const remainingContent = parts[1].substring(parts[1].indexOf('\n\n') + 2);
              // Split by segments and preserve Entrevistador and Out Intro segments
              restPart = remainingContent
                .split('\n\n')
                .filter(segment => {
                  // Always keep Entrevistador segments
                  if (segment.startsWith('Entrevistador |')) {
                    return true;
                  }
                  
                  // Always keep Out Intro segments
                  if (segment.startsWith('Out Intro |')) {
                    return true;
                  }
                  
                  // More comprehensive check for standard text fragments for other segments
                  return !(
                    segment.includes(standardText) ||
                    segment.includes('todas as histórias cabem no sinopse') ||
                    segment.includes('o podcast do agrupamento') ||
                    segment.includes('Escolas de Albergaria a Velha') ||
                    (segment.includes('sinopse') && segment.includes('podcast'))
                  );
                })
                .join('\n\n');
            }
            
            transcriptionText = introPart + (restPart ? '\n\n' + restPart : '');
          } else {
            transcriptionText = contentText;
          }
        } else {
          // No Intro marker in the content, use it as is
          transcriptionText = contentText;
        }
        
        // We've already set transcriptionText above, don't append contentText again
        
        // We don't need to add outro text again as it's already included in the segments
        
        // Ensure correct school name by replacing any incorrect versions
        transcriptionText = transcriptionText.replace(/Escolas d[ae] Obriga e a Avena/g, 'Escolas de Albergaria a Velha');
        transcriptionText = transcriptionText.replace(/Escolas d[ae] Obriga/g, 'Escolas de Albergaria a Velha');
        transcriptionText = transcriptionText.replace(/Escolas d[ae] .{1,20}Avena/g, 'Escolas de Albergaria a Velha');
        
        // Fix the double 'O' issue in the first Entrevistador line
        transcriptionText = transcriptionText.replace(/(Entrevistador \| \d+:\d+)\nOOlá/g, '$1\nOlá');
        
        // Fix the 'O' prefix issue in all speaker segments - only when O is followed by uppercase letter
        transcriptionText = transcriptionText.replace(/((Entrevistador|Convidado|Intro|Out Intro|Outro) \| \d+:\d+)\nO([A-Z])/g, '$1\n$3');
        
        // Fix the trailing 'O' issue in words like 'Obrigada.O'
        transcriptionText = transcriptionText.replace(/([A-Za-z]+\.)O(\s|$)/g, '$1 $2');
        
        // Fix spacing after punctuation marks
        transcriptionText = transcriptionText.replace(/([.,!?:;])([A-Za-z])/g, '$1 $2');
      }
      
      if (!transcriptionText) {
        throw new Error('Nenhum texto de transcrição encontrado na resposta da API');
      }

      // Update transcription record with content and raw response
      const { data: updatedTranscription, error: updateError } = await supabase 
        .from('podcast_transcriptions')
        .upsert({
          id: transcriptionRecord.id,
          podcast_id: podcastId,
          content: transcriptionText,
          language: 'pt',
          status: 'completed',
          error: null,
          raw_response: response.data,
          confidence_score: response.data.confidence_score || null,
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      // Double-check if the transcription was saved
      if (!updatedTranscription) {
        throw new Error('Failed to save transcription to database');
      }

      // Verify the content was saved
      const { data: verifyTranscription } = await supabase
        .from('podcast_transcriptions')
        .select('*')
        .eq('id', transcriptionRecord.id)
        .single();

      if (!verifyTranscription?.content) {
        throw new Error('Transcription content not saved properly');
      }

      if (updateError) {
        console.error('Error updating transcription:', updateError);
        throw updateError;
      }
      console.log('Transcription saved successfully');
      return updatedTranscription as PodcastTranscription;

    } catch (err) {
      console.error('Error generating transcription:', err);
      
      let errorMessage = 'Erro desconhecido ao gerar actranscrição';
      
      if (axios.isAxiosError(err)) {
        if (err.code === 'ECONNABORTED') {
          errorMessage = 'Tempo limite excedido ao gerar a transcrição. Por favor, tente novamente.';
        } else {
          errorMessage = err.response?.data?.detail || err.response?.data?.message || err.message;
        }
        console.error('Axios error details:', {
          status: err.response?.status,
          data: err.response?.data,
          headers: err.response?.headers
        });
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      
      console.error('Error details:', errorMessage);

      // Update status to failed if we have a pending transcription
      if (transcriptionRecord?.id) {
        await supabase
          .from('podcast_transcriptions')
          .update({
            status: 'failed',
            error: errorMessage,
            updated_at: new Date().toISOString()
          })
          .eq('id', transcriptionRecord.id);
      }

      throw new Error(`Erro ao gerar a transcrição: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const generateChapters = async (podcastId: string, transcription: string, audioUrl?: string) => {
    try {
      setLoading(true);
      setError(null);
      
      if (!transcription) {
        throw new Error('Transcription is required for chapter generation');
      }
      
      // First check if chapters already exist
      const { data: existingChapters } = await supabase
        .from('podcast_chapters')
        .select('*')
        .eq('podcast_id', podcastId)
        .order('start_time', { ascending: true });

      if (existingChapters && existingChapters.length > 0) {
        console.log('Chapters already exist');
        return existingChapters as PodcastChapter[];
      }

      console.log('Generating chapters from transcription');
      
      // Parse the transcription to extract speaker segments
      const segments = transcription.split('\n\n');
      const chapterSegments = [];
      
      // Process each segment to identify speakers and timestamps
      for (let i = 0; i < segments.length; i++) {
        const segment = segments[i];
        const match = segment.match(/^([^|]+)\s*\|\s*(\d+:\d+\.\d+)/);
        
        if (match) {
          const speaker = match[1].trim();
          const timestamp = match[2];
          
          // Convert timestamp to seconds for sorting
          const parts = timestamp.split(':');
          const minutes = parseInt(parts[0], 10);
          const seconds = parseFloat(parts[1]);
          const totalSeconds = minutes * 60 + seconds;
          
          // Only create chapters for main speakers (not intro/outro)
          if (speaker !== 'Intro' && speaker !== 'Outro') {
            chapterSegments.push({
              speaker,
              timestamp,
              totalSeconds,
              text: segment.substring(segment.indexOf('\n') + 1)
            });
          }
        }
      }
      
      // Group by speaker and create chapters
      const speakerChanges = [];
      let currentSpeaker = '';
      
      chapterSegments.sort((a, b) => a.totalSeconds - b.totalSeconds);
      
      for (const segment of chapterSegments) {
        if (segment.speaker !== currentSpeaker) {
          currentSpeaker = segment.speaker;
          speakerChanges.push(segment);
        }
      }
      
      // Create chapters from speaker changes
      const chapters = speakerChanges.map((segment, index) => ({
        title: `Parte ${index + 1}`,
        description: `${segment.speaker}`,
        start_time: segment.timestamp
      }));
      
      // If we couldn't extract chapters from transcription and we have an audio URL,
      // fall back to using the Gladia API directly
      if (chapters.length === 0 && audioUrl) {
        console.log('Falling back to Gladia API for chapter generation');
        
        try {
          const formData = new FormData();
          formData.append('audio_url', audioUrl);
          formData.append('language', 'pt');
          formData.append('toggle_diarization', 'true');
          formData.append('diarization_max_speakers', '3');
          
          const response = await axios.post(
            'https://api.gladia.io/audio/text/audio-transcription/',
            formData,
            {
              headers: {
                'x-gladia-key': 'e461554e-4661-4897-8898-85e53e914d2c',
                'Content-Type': 'multipart/form-data'
              }
            }
          );
          
          console.log('Gladia API diarization response:', response.data);
          
          // Process diarization results into chapters
          if (response.data.prediction) {
            const speakerChanges = [];
            let currentSpeaker = '';
            
            // Sort segments by start time
            const sortedSegments = [...response.data.prediction].sort(
              (a, b) => (a.start_time || 0) - (b.start_time || 0)
            );
            
            // Find points where speaker changes
            for (const segment of sortedSegments) {
              const speaker = segment.speaker || '';
              if (speaker && speaker !== currentSpeaker) {
                currentSpeaker = speaker;
                speakerChanges.push({
                  speaker: speaker,
                  start_time: segment.start_time || 0
                });
              }
            }
            
            // Create chapters from speaker changes
            const apiChapters = speakerChanges.map((change, index) => {
              const minutes = Math.floor(change.start_time / 60);
              const seconds = Math.floor(change.start_time % 60);
              const timeStr = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.000`;
              
              return {
                title: `Parte ${index + 1}`,
                description: `Speaker ${change.speaker.replace('SPEAKER_', '')}`,
                start_time: timeStr
              };
            });
            
            if (apiChapters.length > 0) {
              const { data, error } = await supabase
                .from('podcast_chapters')
                .insert(
                  apiChapters.map(chapter => ({
                    podcast_id: podcastId,
                    ...chapter
                  }))
                )
                .select();

              if (error) throw error;
              return data as PodcastChapter[];
            }
          }
        } catch (apiErr) {
          console.error('Error using Gladia API for chapters:', apiErr);
          // Continue with manual chapter creation below
        }
      }
      
      // If we have chapters from transcription parsing, save them
      if (chapters.length > 0) {
        const { data, error } = await supabase
          .from('podcast_chapters')
          .insert(
            chapters.map(chapter => ({
              podcast_id: podcastId,
              ...chapter
            }))
          )
          .select();

        if (error) throw error;
        return data as PodcastChapter[];
      }
      
      // If all else fails, create default chapters
      const defaultChapters = [
        {
          title: 'Parte 1',
          description: 'Introdução',
          start_time: '00:00.000',
          podcast_id: podcastId
        }
      ];
      
      const { data, error } = await supabase
        .from('podcast_chapters')
        .insert(defaultChapters)
        .select();

      if (error) throw error;
      return data as PodcastChapter[];
    } catch (err) {
      console.error('Error generating chapters:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    fetchTranscription,
    fetchChapters,
    generateTranscription,
    generateChapters,
    checkTranscriptionValidity // Export the new function
  };
}

export { usePodcastTranscription };
export default usePodcastTranscription;
